import nexusDexLogo from "@/assets/nexus_dex_logo.png";
import nexusDexLogoMark from "@/assets/nexus_dex_logo_mark.png";

/**
 * Which artwork the mark renders:
 *   - "tile" (default) the full 1:1 chrome compass logo — used everywhere the
 *     logo reads as an app-icon tile (splash, marketing, settings).
 *   - "mark"  a lighter, tighter header crop. PR-D #11 swaps ONLY the AppShell
 *     header to this so the 1 MB tile isn't re-decoded on every page chrome.
 *
 * `nexus_dex_logo_mark.png` is that dedicated asset: 256 px, ~72 KB, built
 * from the 2048 px master.
 *
 * TWO THINGS ABOUT IT ARE DELIBERATE, and a future re-export should preserve
 * both:
 *
 *   - ANTI-ALIASED ALPHA. The previous mark had a hard binary key (0 or 255,
 *     no partial alpha), which shredded thin features when the browser
 *     squashed it into a 30 px box — a segment of the outer ring visibly
 *     dropped out. Feathered edges are what keep the ring intact at size.
 *   - CONTENT FILLS ~94% OF THE CANVAS, matching what it replaced. The master
 *     carries a wider transparent margin, so pasting it in raw would render
 *     the mark ~5% smaller in the same box and silently resize the brand.
 *
 * 256 px covers the largest use (LegalSplash at 48 px) at 3x with headroom,
 * which is the point of the variant: page chrome should not decode the 1 MB
 * tile on every route.
 */
export type LogoVariant = "tile" | "mark";

interface LogoProps {
  size?: number;
  className?: string;
  withWordmark?: boolean;
  tagline?: boolean;
  variant?: LogoVariant;
}

/**
 * Nexus Dex mark — the chrome compass with a neon-green N core.
 *
 * Two sources keep the mark crisp across sizes:
 *   - nexus_dex_logo.png         (square 1:1, full chrome detail) for size > 40px
 *   - nexus_dex_logo_compact.png (tighter crop, simplified halo)  for size <= 40px
 *
 * Rounded with a 22% corner radius so it reads as an app icon in tile contexts.
 * The mark stays the same across light and dark themes: the chrome plating is
 * legible on either background; we just soften the outer halo in light mode so
 * the dark vignette doesn't punch a hole in a white surface (see CSS below).
 */
export function LogoMark({ size = 28, className = "", variant = "tile" }: LogoProps) {
  // "tile" serves the high-resolution official Nexus Dex compass mark — the
  // same artwork shipped as the iOS splash + AppIcon. "mark" serves the lighter
  // header crop so the page chrome doesn't re-decode the 1 MB tile on every
  // route; both scale down crisply.
  const src = variant === "mark" ? nexusDexLogoMark : nexusDexLogo;
  const radius = Math.round(size * 0.22);
  return (
    <img
      src={src}
      width={size}
      height={size}
      alt="Nexus Dex"
      className={`logo-mark ${className}`}
      style={{
        borderRadius: radius,
        display: "inline-block",
        objectFit: "contain",
        boxShadow:
          "0 1px 0 rgba(255,255,255,0.08) inset, 0 0 0 1px rgba(0,0,0,0.4)",
        // Faction accents: the N core's green is baked into the PNG, so the
        // recolour is the shared baked-art hue rotation (0deg on brand green).
        filter: "hue-rotate(var(--silhouette-hue-rotate, 0deg))",
      }}
      draggable={false}
    />
  );
}

export function Logo({ size = 28, className = "", withWordmark = false, tagline = false, variant = "tile" }: LogoProps) {
  return (
    <div className={`inline-flex items-center gap-2.5 ${className}`}>
      <LogoMark size={size} variant={variant} />
      {withWordmark && (
        <div className="flex flex-col leading-none">
          <span
            className="font-bold tracking-tight text-foreground"
            style={{ fontSize: size * 0.6, letterSpacing: "-0.01em" }}
          >
            <span className="text-gradient-chrome-green">Nexus</span> Dex
          </span>
          {tagline && (
            <span
              className="text-muted-foreground/80 mt-1"
              style={{ fontSize: size * 0.32, letterSpacing: "0.04em" }}
            >
              Trainer Companion Hub
            </span>
          )}
        </div>
      )}
    </div>
  );
}
